import { useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import GroupPersonCountMismatchModal from "../../../modal/GroupPersonCountMismatchModal";
import GroupTooManyPeopleModal from "../../../modal/GroupTooManyPeopleModal";
import {
  finishHobbyGroupPhotoCapture,
  type GroupPersonCountMismatch,
} from "../../../utils/hobbyPersonFlow";
import {
  isAiSelfieCaricatureHobbyPostPaymentActive,
  shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
} from "../../../services/stylePromptResolver";

const ExtractPerson = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isExtracting, setIsExtracting] = useState(true);
  const [retryKey, setRetryKey] = useState(0);
  const [personCountMismatch, setPersonCountMismatch] =
    useState<GroupPersonCountMismatch | null>(null);
  const [tooManyPeopleCount, setTooManyPeopleCount] = useState<number | null>(null);
  const extractionStartedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const stationData = useMemo(() => {
    const fromNav = (location.state as { stationData?: Record<string, unknown> } | null)
      ?.stationData;
    if (fromNav && typeof fromNav === "object" && fromNav.type) {
      return fromNav as Record<string, unknown> & { type?: string; name?: string };
    }
    try {
      const selectedRaw = localStorage.getItem("selectedStation");
      const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
      const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
      return fromSelected || fromStorage;
    } catch {
      return {};
    }
  }, [location.state]);

  const routeSlug = useMemo(() => {
    if (
      isAiSelfieCaricatureHobbyPostPaymentActive() ||
      shouldUseHobbyCaricatureFlowForStoredAiSelfieArt()
    ) {
      return "hobby-caricature";
    }
    const t = stationData?.type;
    return typeof t === "string" && t.trim() ? t : "hobby-caricature";
  }, [stationData?.type]);

  const groupImageUrl = useMemo(() => {
    const fromState = (location.state as { groupImageUrl?: string } | null)?.groupImageUrl;
    return typeof fromState === "string" ? fromState.trim() : "";
  }, [location.state]);

  useEffect(() => {
    if (!isExtracting) return;
    const progressInterval = setInterval(() => {
      setProgress((prev) => (prev >= 95 ? prev : prev + 1));
    }, 120);
    return () => clearInterval(progressInterval);
  }, [isExtracting]);

  useEffect(() => {
    if (extractionStartedRef.current) return;
    extractionStartedRef.current = true;

    const run = async () => {
      setIsExtracting(true);
      setError(null);
      setPersonCountMismatch(null);
      setTooManyPeopleCount(null);
      setProgress(0);

      if (!groupImageUrl) {
        if (!mountedRef.current) return;
        setIsExtracting(false);
        setProgress(0);
        setError("Missing group photo. Please go back and try again.");
        return;
      }

      const ok = await finishHobbyGroupPhotoCapture(
        navigate,
        routeSlug,
        groupImageUrl,
        {
          onLoadingChange: (loading) => {
            if (!mountedRef.current) return;
            setIsExtracting(loading);
          },
          onError: (message) => {
            if (!mountedRef.current) return;
            setError(message);
            setProgress(0);
          },
          onPersonCountMismatch: (info) => {
            if (!mountedRef.current) return;
            setPersonCountMismatch(info);
            setIsExtracting(false);
            setProgress(0);
          },
          onTooManyPeople: (found) => {
            if (!mountedRef.current) return;
            setTooManyPeopleCount(found);
            setIsExtracting(false);
            setProgress(0);
          },
        }
      );

      if (!mountedRef.current) return;
      if (ok) {
        setProgress(100);
        setIsExtracting(false);
      }
    };

    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryKey]);

  const goBackToMediaLibrary = useCallback(() => {
    setPersonCountMismatch(null);
    setTooManyPeopleCount(null);
    navigate(`/${routeSlug}/select-media-library`, {
      replace: true,
      state: { groupPhotoFlow: true },
    });
  }, [navigate, routeSlug]);

  const handleBackToMedia = () => {
    goBackToMediaLibrary();
  };

  return (
    <>
      <StationThemeLayout
        className="personHobbySection personHobbySection--creating p-0"
        station={stationData as any}
        showStationLogo
        showBack={false}
      >
        <div className="selectBrand">
          <div className="creatingPersonHobbyContent">
            <div className="heading">Extracting People From Photo</div>
            <div
              className="description px-5 my-4"
              style={{ fontSize: "20px", color: "#fff" }}
            >
              We are finding each person in your group photo. Just a few seconds!
            </div>

            <div className="creatingPersonHobbyProgress my-5">
              <div className="progressBarContainer">
                <div
                  className="progressBar"
                  role="progressbar"
                  style={{
                    width: `${Math.max(progress, progress > 0 ? 4 : 0)}%`,
                    transition: "width 0.1s linear",
                  }}
                  aria-valuenow={Math.round(progress)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                />
              </div>
            </div>

            {error && (
              <div className="small text-danger mt-2" role="alert">
                {error}
              </div>
            )}

            {!isExtracting && !personCountMismatch && tooManyPeopleCount == null && (
              <div className="d-flex align-items-center justify-content-center gap-3 mt-3">
                {error ? (
                  <>
                    <button
                      type="button"
                      className="proceedBTN"
                      onClick={() => {
                        extractionStartedRef.current = false;
                        setRetryKey((v) => v + 1);
                      }}
                    >
                      Retry
                    </button>
                    <button
                      type="button"
                      className="proceedBTN"
                      onClick={handleBackToMedia}
                    >
                      Go back
                    </button>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </StationThemeLayout>

      {personCountMismatch && (
        <GroupPersonCountMismatchModal
          show
          expectedCount={personCountMismatch.expected}
          foundCount={personCountMismatch.found}
          onHide={goBackToMediaLibrary}
        />
      )}

      {tooManyPeopleCount != null && (
        <GroupTooManyPeopleModal
          show
          foundCount={tooManyPeopleCount}
          maxCount={6}
          autoHideMs={4500}
          onHide={goBackToMediaLibrary}
        />
      )}
    </>
  );
};

export default ExtractPerson;
